import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';
import { CloudUpload, Download, Delete } from '@mui/icons-material';
import api from '../../services/api';
import SwalWithA11yFix from '../../services/swalConfig';

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const [uploadingOrderId, setUploadingOrderId] = useState('');

  const getApiErrorMessage = (apiError, fallbackMessage) => {
    const responseData = apiError?.response?.data;
    if (responseData?.message) return responseData.message;
    if (Array.isArray(responseData?.errors) && responseData.errors.length > 0) {
      return responseData.errors[0]?.msg || fallbackMessage;
    }
    return fallbackMessage;
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setError('');
    try {
      const response = await api.get('/admin/orders');
      setOrders(response.data || []);
    } catch (error) {
      console.error('Error fetching admin orders:', error);
      setError(error.response?.data?.message || 'Failed to load orders from server.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'completed') return 'success';
    if (normalized === 'reviewed') return 'info';
    if (normalized === 'cancelled') return 'error';
    if (normalized === 'in-progress') return 'warning';
    if (normalized === 'assigned') return 'info';
    return 'default';
  };

  const getStatusChipSx = (status) => {
    const normalized = (status || '').toLowerCase();
    const styles = {
      pending: { backgroundColor: '#f5f5f5', color: '#424242' },
      assigned: { backgroundColor: '#e3f2fd', color: '#0d47a1' },
      'in-progress': { backgroundColor: '#fff8e1', color: '#f57f17' },
      completed: { backgroundColor: '#e8f5e9', color: '#1b5e20' },
      reviewed: { backgroundColor: '#e3f2fd', color: '#0b5394' },
      cancelled: { backgroundColor: '#ffebee', color: '#b71c1c' },
    };
    return styles[normalized] || { backgroundColor: '#f5f5f5', color: '#616161' };
  };

  const isReportUploaded = (order) => Boolean(order?.fileUrl);

  // "New" for admin: not downloaded by practitioner yet and no report uploaded.
  // (We avoid using only `createdAt` so it still works for older unseen orders.)
  const isNewOrder = (order) => !order?.practitionerDownloadedAt && !isReportUploaded(order);

  const getRowSx = (order) => {
    const normalizedStatus = String(order?.status || '').toLowerCase();
    if (normalizedStatus === 'cancelled') {
      return {
        backgroundColor: 'rgba(198, 40, 40, 0.10)',
        '&:hover': { backgroundColor: 'rgba(198, 40, 40, 0.16)' },
      };
    }
    if (normalizedStatus === 'completed') {
      return {
        backgroundColor: 'rgba(46, 125, 50, 0.10)',
        '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.16)' },
      };
    }
    if (isNewOrder(order)) {
      return {
        backgroundColor: 'rgba(25, 118, 210, 0.10)',
        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.16)' },
      };
    }
    return {
      backgroundColor: '#fff',
      '&:hover': { backgroundColor: '#f9f9f9' },
    };
  };

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      if (statusFilter !== 'all' && order.status !== statusFilter) {
        return false;
      }

      if (!search.trim()) {
        return true;
      }

      const query = search.toLowerCase();
      const patientName = `${order.patient?.firstName || ''} ${order.patient?.lastName || ''}`.toLowerCase();
      const practitionerName = `${order.practitioner?.firstName || ''} ${order.practitioner?.lastName || ''}`.toLowerCase();
      const testName = (order.test?.name || '').toLowerCase();
      const labName = (order.lab?.name || order.test?.lab?.name || '').toLowerCase();

      return (
        patientName.includes(query) ||
        practitionerName.includes(query) ||
        testName.includes(query) ||
        labName.includes(query)
      );
    });
  }, [orders, search, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const paginatedOrders = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return filteredOrders.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredOrders, page]);

  const handleUploadResult = async (orderId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadingOrderId(orderId);
    try {
      await api.post(`/admin/orders/${orderId}/upload-result`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchOrders();
      await SwalWithA11yFix({
        icon: 'success',
        title: 'Result Uploaded',
        text: 'The test result file was uploaded successfully.',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (uploadError) {
      console.error('Error uploading result:', uploadError);
      const uploadErrorMessage = getApiErrorMessage(uploadError, 'Failed to upload result.');
      setError(uploadErrorMessage);
      await SwalWithA11yFix({
        icon: 'error',
        title: 'Upload Failed',
        text: uploadErrorMessage,
      });
    } finally {
      setUploadingOrderId('');
    }
  };

  const handleDownloadResult = async (orderId) => {
    try {
      const response = await api.get(`/admin/orders/${orderId}/download-result`, { responseType: 'blob' });
      const contentDisposition = response.headers?.['content-disposition'] || '';
      const fileNameMatch =
        contentDisposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        contentDisposition.match(/filename="?([^"]+)"?/i);
      const fileName = fileNameMatch?.[1] ? decodeURIComponent(fileNameMatch[1]) : `order-result-${orderId}`;
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (downloadError) {
      console.error('Error downloading result:', downloadError);
      setError(downloadError.response?.data?.message || 'Failed to download result.');
    }
  };

  const handleDeleteResult = async (orderId) => {
    const confirm = await SwalWithA11yFix({
      icon: 'warning',
      title: 'Delete result?',
      text: 'This will remove the uploaded result from the admin, practitioner, and patient views.',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d32f2f',
    });
    if (!confirm.isConfirmed) return;

    try {
      setError('');
      await api.delete(`/admin/orders/${orderId}/result`);
      await fetchOrders();
      await SwalWithA11yFix({
        icon: 'success',
        title: 'Deleted',
        text: 'Result deleted successfully.',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (deleteError) {
      console.error('Error deleting result:', deleteError);
      const deleteErrorMessage = getApiErrorMessage(deleteError, 'Failed to delete result.');
      setError(deleteErrorMessage);
      await SwalWithA11yFix({
        icon: 'error',
        title: 'Delete Failed',
        text: deleteErrorMessage,
      });
    }
  };

  const handleStatusChange = async (orderId, status) => {
    try {
      setError('');
      await api.put(`/admin/orders/${orderId}/status`, { status });
      await fetchOrders();
      await SwalWithA11yFix({
        icon: 'success',
        title: 'Status Updated',
        text: `Order status changed to ${status.replace('-', ' ')}.`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (updateError) {
      console.error('Error updating order status:', updateError);
      const statusErrorMessage = getApiErrorMessage(updateError, 'Failed to update order status.');
      setError(statusErrorMessage);
      await SwalWithA11yFix({
        icon: 'error',
        title: 'Status Update Failed',
        text: statusErrorMessage,
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Search orders"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
          />
          <TextField
            select
            label="Status"
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="assigned">Assigned</MenuItem>
            <MenuItem value="in-progress">In Progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="reviewed">Reviewed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </TextField>
          <Button variant="outlined" size="small" onClick={fetchOrders}>
            Refresh
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label="New order"
            sx={{
              backgroundColor: 'rgba(25, 118, 210, 0.10)',
              border: '1px solid rgba(25, 118, 210, 0.35)',
            }}
          />
          <Chip
            size="small"
            label="Report uploaded"
            sx={{
              backgroundColor: 'rgba(46, 125, 50, 0.10)',
              border: '1px solid rgba(46, 125, 50, 0.35)',
            }}
          />
        </Box>
        {error && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Patient</TableCell>
                <TableCell>Practitioner</TableCell>
                <TableCell>Test</TableCell>
                <TableCell>Lab</TableCell>
                <TableCell>Upload Result</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>First Download</TableCell>
                <TableCell>Order Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedOrders.map((order) => (
                <TableRow key={order._id} hover sx={getRowSx(order)}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {order.patient?.firstName} {order.patient?.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {order.patient?.email || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {order.patient?.phone || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {order.practitioner?.firstName} {order.practitioner?.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {order.practitioner?.email || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {order.practitioner?.phone || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>{order.test?.name || '-'}</TableCell>
                  <TableCell>{order.lab?.name || order.test?.lab?.name || '-'}</TableCell>
                  <TableCell>
                    <Button component="label" size="small" variant="outlined" startIcon={<CloudUpload />} disabled={uploadingOrderId === order._id}>
                      {uploadingOrderId === order._id ? 'Uploading...' : (order.fileUrl ? 'Reupload' : 'Upload')}
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleUploadResult(order._id, e.target.files?.[0])}
                      />
                    </Button>
                    {order.fileUrl && (
                      <>
                        <IconButton size="small" color="primary" onClick={() => handleDownloadResult(order._id)} title="Download result">
                          <Download fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteResult(order._id)} title="Delete result">
                          <Delete fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        label="Status"
                        value={order.status || 'pending'}
                        onChange={(e) => handleStatusChange(order._id, e.target.value)}
                      >
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="assigned">Assigned</MenuItem>
                        <MenuItem value="in-progress">In Progress</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="reviewed">Reviewed</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                      </Select>
                    </FormControl>
                    <Chip
                      size="small"
                      label={(order.status || '').replace('-', ' ')}
                      color={getStatusColor(order.status)}
                      sx={{ ml: 1, ...getStatusChipSx(order.status) }}
                    />
                  </TableCell>
                  <TableCell>
                    {order.practitionerDownloadedAt ? (
                      <>
                        <Chip size="small" color="success" label="Downloaded" sx={{ mb: 0.5 }} />
                        <Typography variant="caption" color="text.secondary" display="block">
                          {new Date(order.practitionerDownloadedAt).toLocaleString()}
                        </Typography>
                      </>
                    ) : (
                      <Chip size="small" variant="outlined" label="Not downloaded" />
                    )}
                  </TableCell>
                  <TableCell>
                    {order.createdAt ? (
                      <>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {new Date(order.createdAt).toLocaleTimeString()}
                        </Typography>
                        <Typography variant="body2">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </Typography>
                      </>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredOrders.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
            No orders found for the current filters.
          </Typography>
        )}

        {filteredOrders.length > 0 && (
          <Box
            sx={{
              pt: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {((page - 1) * itemsPerPage) + 1} - {Math.min(page * itemsPerPage, filteredOrders.length)} of {filteredOrders.length}
            </Typography>
            <Pagination
              count={Math.ceil(filteredOrders.length / itemsPerPage)}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default OrderManagement;
